<!--suppress HtmlUnknownBooleanAttribute -->
<template>
    <div>
        <table-paginator :cardsDisplayMode="cardsDisplayMode"
                         :firstDisplayedEntryIndex.sync="firstDisplayedEntryIndex"
                         :numRowsPerPage.sync="numRowsPerPage"
                         :renderedRows="renderedEntries.length"
                         attachHtmlId="t_pag_top"
                         @card-button-click="cardsDisplayMode = !cardsDisplayMode"
        >
            <template #prepend>

                <square-button :disabled="loading"
                               :loading="loading"
                               :tooltip-text="t_reload"
                               flat
                               icon="fas fa-arrows-rotate"
                               @click="$emit('refresh')" />

                <v-select
                    :attach="`.v-select-select-columns-dropdown`"
                    :items="columnOptions"
                    :value="selectedColumns"
                    class="v-select-select-columns-dropdown v-select-max-width-min-content"
                    dense
                    hide-details="auto"
                    item-text="label"
                    multiple
                    solo
                    aria-label="number of columns shown"
                    @input="$emit('update:selectedColumns', $event)">
                    <template #selection>
                        <!-- Empty on purpose -->
                    </template>
                    <template #prepend-inner>
                            {{t_columns_selected }}
                    </template>
                    <template #prepend-item>
                        <v-list-item
                            ripple
                            style="user-select: none"
                            @click="onSelectAllColumnsItemClick"
                            @mousedown.prevent>
                            <v-list-item-action>
                                <v-checkbox
                                    :input-value="allColumnsSelected"
                                    style="pointer-events: none;">
                                </v-checkbox>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ t_select_all_option }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                    </template>
                    <template #item="{ item, on, attrs}">
                        <v-list-item
                            :disabled="item.disabled"
                            ripple
                            style="user-select: none"
                            v-bind="item.disabled ? undefined : attrs"
                            v-on="item.disabled ? undefined : on"
                            @mousedown.prevent>
                            <v-list-item-action>
                                <v-checkbox
                                    :disabled="item.disabled"
                                    :input-value="selectedColumns.includes(item.value)"
                                    style="pointer-events: none;"
                                ></v-checkbox>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ item.label }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-select>
            </template>
        </table-paginator>


        <p-data-table
            v-if="tableDisplayed"
            :id="tableElementRef"
            :ref="tableElementRef"
            :class="tableClassMapComputed"
            :filters="filters"
            :first.sync="firstDisplayedEntryIndex"
            :loading="loading"
            :paginator="true"
            :paginatorTemplate="''"
            :rows="numRowsPerPage"
            :selection="selectedEntriesInternal"
            :stateKey="dataTableStateKey"


            :value="entries"
            columnResizeMode="fit"
            compareSelectionBy="equals"
            dataKey="id"

            filterDisplay="row"
            :filter-locale="filterLocale"

            loadingIcon="far fa-spinner-third fa-spin"
            removableSort
            :sort-field="sortFieldInitial"
            :sort-order="sortOrderInitial"

            resizableColumns
            responsiveLayout="scroll"
            stateStorage="local"

            :tableStyle="tableStyleWithAdditionalStyles"

            @sort="onTableSort"
            @value-change="onTableValueChange"


            @state-restore="onTableStateRestore"

            @update:selection="$emit('update:selectedEntries', $event)"
            @row-contextmenu="$emit('row-contextmenu', $event)"
            @scroll.passive="onTableScroll"
            @column-resize-end="onColumnResizeEnd"
        >
            <!--suppress VueUnrecognizedSlot -->
            <template #loading>
                <div class="table-text-center">{{ textLoading }}</div>
            </template>

            <!--suppress VueUnrecognizedSlot -->
            <template #empty>
                <table-empty-message v-bind="tableEmptyMessageProps"
                                     @clearFilters="$emit('clearFilters')"
                />
            </template>

            <slot :rowDropdownClick="rowDropdownClick" />
        </p-data-table>

        <table-paginator :cardsDisplayMode="cardsDisplayMode"
                         :firstDisplayedEntryIndex.sync="firstDisplayedEntryIndex"
                         :numRowsPerPage.sync="numRowsPerPage"
                         :renderedRows="renderedEntries.length"
                         attachHtmlId="t_pag_bot"
                         menuOnTop
                         @card-button-click="cardsDisplayMode = !cardsDisplayMode"
        />

        <template v-if="tableDisplayed && cardsDisplayMode">
            <teleport :to="`#${tableElementRef} > .p-datatable-wrapper > .p-datatable-table`">
                <cards-container :firstDisplayedEntryIndex="firstDisplayedEntryIndex"
                                 :getEditLink="getEditLink"
                                 :loading="loading"
                                 :menuDropdownItems="rowDropdownItems"
                                 :numRowsPerPage="numRowsPerPage"
                                 :openMenuId="openMenuId"
                                 :renderedEntries="renderedEntries"
                                 :selectedEntries="selectedEntriesInternal"
                                 :selectedRowIdsMap="selectedRowIdsMap"
                                 @selected="$emit('rowSelect', $event)"
                                 @unselected="$emit('rowUnselect', $event)"
                                 @edit-button-click="$emit('edit-button-click', $event)"
                                 @menu-button-click="id => rowDropdownClick(id, true)"
                                 @thumbnailPreviewClick="$emit('thumbnailPreviewClick', $event)"
                >

                    <!--suppress VueUnrecognizedSlot -->
                    <template #loading>
                        <div class="table-text-center">{{ textLoading }}</div>
                    </template>

                    <!--suppress VueUnrecognizedSlot -->
                    <template #empty>
                        <table-empty-message v-bind="tableEmptyMessageProps"
                                             @clearFilters="$emit('clearFilters')"
                        />
                    </template>
                </cards-container>
            </teleport>
        </template>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import { delay } from '../../../../utility'
import PrimeVueDataTable from '../PrimeVue/DataTable.vue'
import TablePaginator from './table-paginator.vue'
import CardsContainer from './cards-container.vue'
import TableEmptyMessage from './table-empty-message.vue'

import Teleport from 'vue2-teleport'
import ListDropdownMenu from '../list-dropdown-menu.vue'
import SquareButton from '../squareButton.vue'

export default defineComponent({
    components: {
        SquareButton,
        ListDropdownMenu,
        PDataTable: PrimeVueDataTable,
        TablePaginator,
        CardsContainer,
        TableEmptyMessage,
        Teleport
    },
    props: {
        tableElementRef: {
            type: String,
            required: true
        },
        entries: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            required: true
        },
        localStorageKeyPrefix: {
            type: String,
            required: true
        },
        /** @type {{label: string, value: string, disabled?: boolean}[]} */
        columnDefaults: {
            type: Array,
            required: true
        },
        /** @type {{label: string, value: string, width: number, disabled?: boolean}[]} */
        columnOptions: {
            type: Array,
            required: true
        },
        selectedColumns: {
            type: Array,
            required: true
        },
        selectedRowIdsMap: {
            type: Object,
            required: true
        },
        selectedEntries: {
            type: Array,
            required: true
        },
        filters: {
            type: Object,
            required: true
        },
        hiddenEntriesDisplayed: {
            type: Boolean,
            required: true
        },
        columnsToHideInCardMode: {
            type: Array,
            default: []
        },
        allFilterableColumnsSelected: {
            type: Boolean,
            default: true
        },
        textLoading: {
            type: String,
            required: true
        },
        textNoEntriesFound: {
            type: String,
            required: true
        },
        textNoEntriesFromBackend: {
            type: String,
            required: true
        },
        noEntriesFromBackend: {
            type: Boolean,
            required: true
        },
        filtersEmpty: {
            type: Boolean,
            required: true
        },
        openMenuId: {
            type: [Number, null]
            //required: true
        },
        rowDropdownItems: {
            type: Array,
            default: []
        },
        breakoutFullWidth: {
            type: Boolean,
            default: true
        },
        getEditLink: {
            type: Function,
            required: true
        },
        tableStyle: {
            type: Object,
            default: null
        },
        tableClassMap: {
            type: Object,
            default: null
        },
        sortFieldInitial: String,
        sortOrderInitial: Number,
    },
    data() {
        return {
            renderedEntries: [],

            selectedEntriesInternal: [],

            firstDisplayedEntryIndex: 0,

            sortField: null,
            sortOrder: null,

            displayedColumns: {},

            textAreaStyles: {},
            textAreaStyleValues: {},

            tableDisplayed: true,
            cardsDisplayMode: true,

            numRowsPerPage: 25,

            tableScrolledLeft: true,
            tableScrolledRight: false,
            resizeObserverCloseCallback: () => {},

            columnPreviewWidth: 0,

            t_reload: Lang.langGet('Reload'),
            t_all: Lang.langGet('All'),
            t_select_all: Lang.langGet('Select All'),
            t_deselect_all: Lang.langGet('Deselect All'),
        }
    },
    created() {
        this.cardsDisplayMode = localStorage.getItem(this.localStorageKeyCardsDisplayMode) === 'true';
        this.displayedColumns = {}
        this.selectedColumns.forEach((selectedColumn) => this.displayedColumns[selectedColumn] = true)
    },
    async mounted() {
        if (this.cardsDisplayMode) {
            await this.$nextTick()
            this.changeTableViewToCards()
        }

        const resizeObserver = new ResizeObserver((entries) => {
            this.calcTableScrolledPosition(entries[0].target);
        });

        await delay(16);
        resizeObserver.observe(this.$refs[this.tableElementRef].$el.getElementsByClassName('p-datatable-wrapper')[0]);

        this.resizeObserverCloseCallback = () => resizeObserver.unobserve(this.$refs[this.tableElementRef].$el.getElementsByClassName('p-datatable-wrapper')[0]);
    },
    beforeDestroy() {
        this.resizeObserverCloseCallback();
    },
    computed: {
        columnDefaultsValues() {
            return this.columnDefaults.map(col => col.value)
        },
        dataTableStateKey() {
            return `${this.localStorageKeyPrefix}-state.${Laravel.activeUser}.${Laravel.activeProject}`
        },
        localStorageKeyCardsDisplayMode() {
            return `${this.localStorageKeyPrefix}-cards-display-mode.${Laravel.activeUser}.${Laravel.activeProject}`
        },
        allColumnsSelected() {
            return this.selectedColumns.length == this.columnOptions.length;
        },
        filterLocale() {
            return Laravel.activeLocale;
        },
        tableEmptyMessageProps() {
            return {
                allFilterableColumnsSelected: this.allFilterableColumnsSelected,
                filtersEmpty: this.filtersEmpty,
                message: this.noEntriesFromBackend ? this.textNoEntriesFromBackend : this.textNoEntriesFound
            };
        },
        tableClassMapComputed() {
            return {
                ...(this.tableClassMap ?? {}),
                'breakout-full-width': this.breakoutFullWidth,
                'table-scrolled-left': this.tableScrolledLeft,
                'table-scrolled-right': this.tableScrolledRight
            };
        },
        tableStyleWithAdditionalStyles() {
           return {
               '--column-preview-width-ul': this.columnPreviewWidth,
               ...this.tableStyle,
           }
        },
        t_columns_selected() {
            return Lang.langGet(':number columns shown',
                {
                    number: this.allColumnsSelected
                        ? this.t_all
                        : `${this.selectedColumns.length} / ${this.columnOptions.length}`
                }
            );
        },
        t_select_all_option() {
            return this.allColumnsSelected ? this.t_deselect_all : this.t_select_all;
        }
    },
    methods: {
        async rowDropdownClick(id, positionEnd) {
            //console.log('menu toggled with id', id)
            this.$emit('update:openMenuAtEnd', !!positionEnd)
            //document.removeEventListener('click', this.clickListenerMenu)


            if (this.openMenuId === id) {
                return this.$emit('update:openMenuId', null)
            }

            this.$emit('update:openMenuId', id)
        },
        async onSelectedColumnsChange(newColumns, hiddenColumns = this.cardsDisplayMode ? this.columnsToHideInCardMode : []) {

            const newDisplayedColumns = {};

            newColumns.forEach(newColumn => {
                if (!hiddenColumns.includes(newColumn)) {
                    newDisplayedColumns[newColumn] = true
                }
            })

            this.displayedColumns = newDisplayedColumns;
        },
        async changeTableViewToCards() {
            await this.$nextTick()
            this.columnsToHideInCardMode.forEach(columnToHide => {
                delete this.displayedColumns[columnToHide]
            })
            await this.onSelectedColumnsChange(this.selectedColumns)

            const el = this.$refs[this.tableElementRef].$el
            el.querySelector('tbody').remove()
            el.querySelector('tfoot').remove()
        },
        async changeTableViewToRows() {
            this.tableDisplayed = false
            await this.$nextTick()
            await this.onSelectedColumnsChange(this.selectedColumns)
            this.tableDisplayed = true
            await this.$nextTick()
            this.selectedEntriesInternal = [...this.selectedEntriesInternal]
        },
        onTableSort(sortEvent) {
            this.sortField = sortEvent.sortField ?? this.sortFieldInitial;
            this.sortOrder = sortEvent.sortOrder ?? this.sortOrderInitial;
        },
        onTableValueChange(value) {
            if (this.cardsDisplayMode && value && value.length > 0 && this.sortField) {
                value.sort((a, b) => {
                    const compA = a[this.sortField];
                    const compB = b[this.sortField];
                    if (compA > compB) return 1 * this.sortOrder;
                    if (compA < compB) return -1 * this.sortOrder;
                    return 0;
                });
            }
            this.renderedEntries = value
            const renderedEntriesMap = {}
            value.forEach(entry => renderedEntriesMap[entry.id] = true)

            this.$emit('update:renderedEntriesMap', renderedEntriesMap)
            this.$emit('update:selectedEntries', this.selectedEntries.filter(selectedEntry => renderedEntriesMap[selectedEntry.id]))
        },
        onTableStateRestore(event) {
            this.firstDisplayedEntryIndex = event.first
            if(event.rows) {
                this.numRowsPerPage = event.rows;
            }

            this.onTableSort(event)
        },
        onTableScroll(event) {
            this.calcTableScrolledPosition(event.target);
        },
        calcTableScrolledPosition(element) {
            this.tableScrolledLeft = element.scrollLeft < 1;
            this.tableScrolledRight = Math.ceil(element.scrollLeft + 2) >= (element.scrollWidth - element.clientWidth);
        },
        onSelectAllColumnsItemClick() {
            if(this.allColumnsSelected) {
                this.$emit('update:selectedColumns', this.columnOptions.filter(columnOption => columnOption.disabled).map(columnOption => columnOption.value));
            } else {
                this.$emit('update:selectedColumns', this.columnOptions.map(columnOption => columnOption.value));
            }
        },
        /**
         * @param {Object} val
         * @param {HTMLElement} val.element
         * @param {number} val.delta
         *  */
        onColumnResizeEnd({element, delta}) {
            if (element.dataset.columnField != 'preview') {
                return;
            }
            this.columnPreviewWidth = +element.style.width.replace('px', '');
        }
    },
    watch: {
        numRowsPerPage() {
            this.firstDisplayedEntryIndex = 0;
        },
        cardsDisplayMode(val) {
            this.$emit('update:cardsDisplayMode', val)
            if (val) {
                this.changeTableViewToCards()
            } else {
                this.changeTableViewToRows()
            }
            localStorage.setItem(this.localStorageKeyCardsDisplayMode, val)
        },
        selectedEntries(val) {
            this.selectedEntriesInternal = val
        },
        selectedColumns(val) {
            this.onSelectedColumnsChange(val)
        },
        displayedColumns(val) {
            this.$emit('update:displayedColumns', val)
        },
    }
})
</script>
